import { apiClient, apiGet, apiPost, apiDelete } from './client';
import type {
  Knowledge,
  KnowledgeLink,
  KnowledgeMarkdown,
  KnowledgePdf,
  OperationResult,
  PagedItemsResult,
} from './types';

// ── Knowledge (the KB record) ─────────────────────────────────────────────────

export interface KnowledgeSearchParams {
  search?: string;
  pageNo?: number;
  pageSize?: number;
}

export async function searchKnowledge(
  params: KnowledgeSearchParams = {}
): Promise<PagedItemsResult<Knowledge>> {
  const { search = '', pageNo = 1, pageSize = 100 } = params;
  const wrapped = await apiGet<OperationResult<PagedItemsResult<Knowledge>>>(
    '/api/Knowledge/Search',
    { search, pageNo, pageSize }
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load knowledge bases');
  }
  return wrapped.data;
}

export async function getKnowledgeById(id: number): Promise<Knowledge> {
  const wrapped = await apiGet<OperationResult<Knowledge>>(`/api/Knowledge/GetById/${id}`);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || `Knowledge ${id} not found`);
  }
  return wrapped.data;
}

// Important: the backend returns OperationResult<Knowledge> with the *saved entity*
// (Id populated by EF). We do NOT conflate `isSuccess === true && data == null` with
// failure — that conflation is what produced the famous "Operation Completed
// Successfully" red-error toast: the backend's default success message string would
// surface as the thrown error text. Check `isSuccess` first; only treat missing
// `data` as a defensible warning, not a hard failure.
export async function createKnowledge(input: { title: string; description?: string }): Promise<Knowledge> {
  const wrapped = await apiPost<OperationResult<Knowledge>>('/api/Knowledge/Add', input);
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || 'Failed to create knowledge base');
  }
  if (!wrapped.data) {
    throw new Error('Knowledge base was created but the server did not return it. Refresh the list.');
  }
  return wrapped.data;
}

export async function updateKnowledge(id: number, input: Partial<Knowledge>): Promise<Knowledge> {
  const wrapped = await apiPost<OperationResult<Knowledge>>(`/api/Knowledge/Update/${id}`, input);
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || 'Failed to update knowledge base');
  }
  if (!wrapped.data) {
    throw new Error('Update saved but the server did not return the updated record. Refresh to see latest.');
  }
  return wrapped.data;
}

export async function removeKnowledge(id: number): Promise<void> {
  const wrapped = await apiDelete<OperationResult>(`/api/Knowledge/Remove/${id}`);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to delete knowledge base');
}

/**
 * Re-runs the Azure AI Search indexing pipeline for an existing source. Used by
 * the "Reindex" row action that surfaces in the Sources table when a row is
 * Pending (DocumentId is null) — typically because the original upload's index
 * step hit a transient Azure AI Search 503 that exhausted retries. Backend
 * dispatches by `kind` and re-runs `IndexXxxContentAsync` against the existing
 * blob (or, for links, re-fetches the URL). Server-side AzureSearchRetry
 * handles transient failures with exponential backoff.
 */
export async function reindexSource(
  kind: 'pdf' | 'markdown' | 'link',
  id: number
): Promise<void> {
  const wrapped = await apiPost<OperationResult>(
    `/api/Knowledge/Reindex/${kind}/${id}`,
    {}
  );
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || 'Reindex failed');
  }
}

/**
 * Update metadata on an existing PDF source. The backend Update endpoint takes
 * the FULL entity (not a patch); the EditSourceDrawer reads the source first,
 * mutates the editable fields (title, description, tags), and posts back.
 *
 * Re-indexing is server-side: the manager re-runs IndexPdfContentAsync from the
 * stored blob so updated tags propagate into Azure AI Search filters.
 */
export async function updateKnowledgePdf(id: number, model: KnowledgePdf): Promise<void> {
  const wrapped = await apiPost<OperationResult>(`/api/KnowledgePdf/Update/${id}`, model);
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || 'Failed to update PDF source');
  }
}

/**
 * Update metadata on an existing Markdown source. Server-side re-indexing flow
 * mirrors PDF (re-runs IndexMarkdownContentAsync from blob).
 */
export async function updateKnowledgeMarkdown(id: number, model: KnowledgeMarkdown): Promise<void> {
  const wrapped = await apiPost<OperationResult>(`/api/KnowledgeMarkdown/Update/${id}`, model);
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || 'Failed to update Markdown source');
  }
}

/**
 * Update metadata on an existing Link source. The link's content is re-fetched
 * from the URL on the server (IndexLinkContentAsync re-runs WebContentExtractor
 * and re-chunks).
 */
export async function updateKnowledgeLink(id: number, model: KnowledgeLink): Promise<void> {
  const wrapped = await apiPost<OperationResult>(`/api/KnowledgeLink/Update/${id}`, model);
  if (!wrapped.isSuccess) {
    throw new Error(wrapped.message || 'Failed to update Link source');
  }
}

// ── KnowledgePdf ──────────────────────────────────────────────────────────────

export async function searchKnowledgePdfs(knowledgeId: number, pageSize = 200): Promise<KnowledgePdf[]> {
  const wrapped = await apiGet<OperationResult<PagedItemsResult<KnowledgePdf>>>(
    '/api/KnowledgePdf/Search',
    { knowledgeId, pageNo: 1, pageSize }
  );
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to load PDFs');
  return wrapped.data.data ?? [];
}

/**
 * Multipart upload: POST /api/KnowledgePdf/Add
 *   - file   : IFormFile
 *   - data   : JSON-stringified KnowledgePdf metadata (KnowledgeId, Title, etc.)
 */
export async function uploadPdf(
  knowledgeId: number,
  file: File,
  metadata: Partial<KnowledgePdf> = {},
  onProgress?: (percent: number) => void
): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append(
    'data',
    JSON.stringify({
      knowledgeId,
      title: metadata.title ?? file.name.replace(/\.pdf$/i, ''),
      description: metadata.description,
      citationDisplayName: metadata.citationDisplayName,
      tags: metadata.tags ?? [],
    })
  );
  await apiClient.post('/api/KnowledgePdf/Add', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (e.total && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
}

export async function removePdf(id: number): Promise<void> {
  const wrapped = await apiDelete<OperationResult>(`/api/KnowledgePdf/Remove/${id}`);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to delete PDF');
}

// ── KnowledgeMarkdown ─────────────────────────────────────────────────────────

export async function searchKnowledgeMarkdown(
  knowledgeId: number,
  pageSize = 200
): Promise<KnowledgeMarkdown[]> {
  const wrapped = await apiGet<OperationResult<PagedItemsResult<KnowledgeMarkdown>>>(
    '/api/KnowledgeMarkdown/Search',
    { knowledgeId, pageNo: 1, pageSize }
  );
  if (!wrapped.isSuccess || !wrapped.data)
    throw new Error(wrapped.message || 'Failed to load Markdown sources');
  return wrapped.data.data ?? [];
}

/**
 * Thrown when the backend rejects an upload because a Markdown source with the
 * same Title already exists in the same KnowledgeBase. Distinct from a generic
 * Error so the UI can offer a "Replace existing" action rather than a blind
 * "Retry" that would fail the same way.
 */
export class DuplicateMarkdownError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DuplicateMarkdownError';
  }
}

export async function uploadMarkdown(
  knowledgeId: number,
  file: File,
  metadata: Partial<KnowledgeMarkdown> = {},
  onProgress?: (percent: number) => void
): Promise<KnowledgeMarkdown> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append(
    'data',
    JSON.stringify({
      knowledgeId,
      title: metadata.title ?? file.name.replace(/\.(md|markdown)$/i, ''),
      description: metadata.description,
      citationDisplayName: metadata.citationDisplayName,
      tags: metadata.tags ?? [],
    })
  );
  const res = await apiClient.post<OperationResult<KnowledgeMarkdown>>(
    '/api/KnowledgeMarkdown/Add',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (e.total && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    }
  );
  const wrapped = res.data;
  if (!wrapped.isSuccess) {
    // The backend returns a duplicate as a 200 OK with isSuccess=false and a
    // message starting with "A Markdown source titled". Detect that explicitly
    // so the UI can route to a Replace-existing flow rather than a blind retry.
    const msg = wrapped.message || 'Failed to upload Markdown';
    if (msg.startsWith('A Markdown source titled')) {
      throw new DuplicateMarkdownError(msg);
    }
    throw new Error(msg);
  }
  if (!wrapped.data) {
    throw new Error('Upload succeeded but the server did not return the saved record. Refresh to confirm.');
  }
  return wrapped.data;
}

/**
 * Replace the file content of an existing Markdown source. Used by the
 * duplicate-resolution flow on multi-file uploads ("this file already exists —
 * replace its content?"). The Knowledge record's metadata (title, description,
 * tags) is preserved; only the file body and its indexed chunks are swapped.
 */
export async function replaceMarkdownFile(
  markdownId: number,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  await apiClient.post(`/api/KnowledgeMarkdown/ReplaceFile/${markdownId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (e.total && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
}

export async function removeMarkdown(id: number): Promise<void> {
  const wrapped = await apiDelete<OperationResult>(`/api/KnowledgeMarkdown/Remove/${id}`);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to delete Markdown source');
}

// ── KnowledgeLink ─────────────────────────────────────────────────────────────

export async function searchKnowledgeLinks(
  knowledgeId: number,
  pageSize = 200
): Promise<KnowledgeLink[]> {
  const wrapped = await apiGet<OperationResult<PagedItemsResult<KnowledgeLink>>>(
    '/api/KnowledgeLink/Search',
    { knowledgeId, pageNo: 1, pageSize }
  );
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to load links');
  return wrapped.data.data ?? [];
}

export async function addLink(input: {
  knowledgeId: number;
  onlineLink: string;
  title?: string;
  description?: string;
  tags?: string[];
}): Promise<void> {
  const wrapped = await apiPost<OperationResult>('/api/KnowledgeLink/Add', {
    knowledgeId: input.knowledgeId,
    onlineLink: input.onlineLink,
    title: input.title ?? input.onlineLink,
    description: input.description,
    tags: input.tags ?? [],
  });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to add link');
}

export async function removeLink(id: number): Promise<void> {
  const wrapped = await apiDelete<OperationResult>(`/api/KnowledgeLink/Remove/${id}`);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to delete link');
}

// ── Helper: classify a file by extension ──────────────────────────────────────

export type UploadKind = 'pdf' | 'markdown' | 'unsupported';

export function classifyFile(file: File): UploadKind {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return 'pdf';
  if (name.endsWith('.md') || name.endsWith('.markdown') || name.endsWith('.txt')) return 'markdown';
  return 'unsupported';
}
